<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useDark, useResizeObserver } from '@vueuse/core';
import * as monaco from 'monaco-editor';
import { useWorkspaceContext } from '@/composables/use-workspace-context';
import { usePanelHandle } from '@/composables/use-panels';
import { getOrCreateModel, getLanguageForPath } from '@/composables/editor-models';
import { monoFont, monoFontSize } from '@/composables/use-editor-theme';
import {
	ensureShiki,
	ensureLanguage,
	updateShikiTheme,
	currentThemeName,
} from '@/composables/use-shiki';
import { Progress } from '@/components/ui/progress';

const props = defineProps<{
	path: string;
}>();

const ctx = useWorkspaceContext();
const isDark = useDark();

const fileName = props.path.split('/').pop() ?? props.path;
const handle = usePanelHandle(fileName);

const editorRef = ref<HTMLElement | null>(null);
let editor: monaco.editor.IStandaloneCodeEditor | null = null;

// layout the editor whenever its container resizes — useResizeObserver auto-cleans on unmount.
// with display:none on hidden panels, ResizeObserver won't fire while hidden and will
// automatically fire with correct dimensions when the panel becomes visible again.
useResizeObserver(editorRef, () => editor?.layout());

// sync dirty state from openFiles to handle
watch(
	() => ctx.openFiles.value.find((f) => f.path === props.path),
	(file) => {
		if (!file) return;
		handle.dirty = file.dirty;
	},
	{ deep: true },
);

function doSave() {
	handle.saveProgress = 0;
	ctx.saveFile(props.path, (percent) => {
		handle.saveProgress = percent;
	});

	// clear progress bar once the save completes
	const stop = watch(
		() => ctx.openFiles.value.find((f) => f.path === props.path)?.saving,
		(saving) => {
			if (!saving) {
				handle.saveProgress = null;
				stop();
			}
		},
	);
}

async function initEditor(content: string) {
	if (!editorRef.value || editor) return;

	const language = getLanguageForPath(props.path);

	// ensure shiki is loaded and the language grammar is available
	await ensureShiki();
	await ensureLanguage(language);

	const model = getOrCreateModel(props.path, content, language);

	// wire up content change tracking
	model.onDidChangeContent(() => {
		ctx.updateFileContent(props.path, model.getValue());
	});

	editor = monaco.editor.create(editorRef.value, {
		model,
		automaticLayout: false,
		theme: currentThemeName(),
		minimap: { enabled: false },
		fontSize: monoFontSize(),
		fontFamily: monoFont(),
		tabSize: 4,
		insertSpaces: false,
		renderWhitespace: 'selection',
		smoothScrolling: true,
		padding: { top: 8 },
		wordWrap: 'on',
	});

	// use addAction (not addCommand) — addAction automatically scopes its keybinding
	// to this editor instance via editorId context key, so only the focused editor fires.
	editor.addAction({
		id: 'save-file',
		label: 'Save File',
		keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
		run: () => doSave(),
	});
}

// focus the monaco editor when the panel receives focus
handle.onFocus = () => {
	if (!editor) return;
	if (!editor.hasTextFocus() && !editor.hasWidgetFocus()) {
		editor.focus();
	}
};

onMounted(async () => {
	if (!editorRef.value) return;

	// always load file content via SFTP — report progress to panel handle
	handle.loadProgress = 0;
	const ok = await ctx.openFilePath(props.path, (percent) => {
		handle.loadProgress = percent;
	});

	if (!ok) {
		handle.loadProgress = null;
		handle.error = 'Failed to load file — SFTP session unavailable or file not accessible.';
		return;
	}

	handle.loadProgress = null;

	const file = ctx.openFiles.value.find((f) => f.path === props.path);
	if (file) {
		initEditor(file.content);
	}
});

onUnmounted(() => {
	editor?.dispose();
	editor = null;
	// model is NOT disposed here — kept alive for undo history across tab switches
	// models are cleaned up via disposeModel() when panels are closed
});

// switch theme when dark mode changes
watch(isDark, () => {
	updateShikiTheme();
	monaco.editor.setTheme(currentThemeName());
});
</script>

<template>
	<div class="relative size-full">
		<div
			v-if="handle.loadProgress != null"
			class="flex flex-col size-full items-center justify-center gap-2 px-16"
		>
			<Progress :model-value="handle.loadProgress" class="max-w-64" />
			<span class="text-xs text-muted-foreground">{{ handle.loadProgress }}%</span>
		</div>
		<div
			v-else-if="handle.error"
			class="flex h-full items-center justify-center text-sm text-destructive"
		>
			{{ handle.error }}
		</div>
		<div
			ref="editorRef"
			class="h-full w-full"
			:class="{ invisible: handle.loadProgress != null }"
		/>
	</div>
</template>

<style>
/* match widget font size (suggest, hover, find/replace) to editor font size — unscoped so it
   reaches Monaco's absolutely-positioned overlays that render outside the component root */
.monaco-editor .editor-widget {
	font-size: var(--font-mono-size);
}
</style>
